import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import useAssignTranscriptSpeaker from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/SpeakerAssignmentSidebar/Hooks/useAssignTranscriptSpeaker';
import { css } from '@emotion/react';
import * as React from 'react';
import { useSelector } from '@verclam/synapse';
import { Button, Dropdown, Form, InputText } from '@verclam/ui-kit';

const MAX_DISPLAY_NAME_LENGTH = 160;
const REQUIRED_MESSAGE = 'Le nom de cet intervenant est requis.';
const ROLE_REQUIRED_MESSAGE = 'Le rôle de cet intervenant est requis.';
const TOO_LONG_MESSAGE = `Le nom ne peut pas dépasser ${MAX_DISPLAY_NAME_LENGTH} caractères.`;

const rowStyle = css`
	display: flex;
	align-items: flex-end;
	gap: 0.5rem;
	flex-wrap: wrap;
`;

const fieldStyle = css`
	flex: 1;
	min-width: 10rem;
`;

const noteStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: 0.78125rem;
	color: ${theme.colors.inkFaint};
`;

type Props = {
	readonly facilityId: number;
	readonly subject: SubjectAddress;
	readonly interviewId: number;
	readonly speakerId: number;
};

/**
 * `kind = OTHER` — the free-text entry point, validated in line (non-empty, ≤160 characters). It is
 * also how a professional gets attributed by name: the inventory never makes them an entity target
 * (D-2), so their only path is this field.
 */
const SpeakerAssignmentFreeNameField: React.FC<Props> = ({ facilityId, subject, interviewId, speakerId }) => {
	const assign = useAssignTranscriptSpeaker(facilityId, subject, interviewId, speakerId);

	// Read, never fetched: the route's guard has already loaded the types before a dossier screen opens.
	const types = useSelector((state) => state.subjectTypesStore.types);
	const speakerRoles = types?.findByKey(subject.typeKey)?.speakerRoles ?? [];
	const hasRoles = speakerRoles.length > 0;

	const [displayName, setDisplayName] = React.useState('');
	const [roleKey, setRoleKey] = React.useState<string | null>(null);
	const [isTouched, setIsTouched] = React.useState(false);

	const formId = React.useId();

	const trimmedDisplayName = displayName.trim();
	const errorMessage =
		trimmedDisplayName.length === 0 && !hasRoles
			? REQUIRED_MESSAGE
			: trimmedDisplayName.length > MAX_DISPLAY_NAME_LENGTH
				? TOO_LONG_MESSAGE
				: null;
	const roleErrorMessage = hasRoles && roleKey === null ? ROLE_REQUIRED_MESSAGE : null;

	const handleRoleChange = React.useCallback((value: string | null) => {
		setRoleKey(value);
	}, []);

	const handleDisplayNameChange = React.useCallback((value: string) => {
		setDisplayName(value);
	}, []);

	// `Form` calls `event.preventDefault()` itself before invoking `onSubmit()`, which never receives
	// the event (`Form.doc.md`).
	const handleSubmit = React.useCallback(() => {
		setIsTouched(true);

		if (errorMessage !== null || roleErrorMessage !== null) {
			return;
		}

		if (hasRoles) {
			assign('role', null, trimmedDisplayName.length > 0 ? trimmedDisplayName : null, roleKey);

			return;
		}

		assign('other', null, trimmedDisplayName, null);
	}, [assign, errorMessage, roleErrorMessage, hasRoles, roleKey, trimmedDisplayName]);

	return (
		<Form id={formId} onSubmit={handleSubmit} aria-label="Attribuer à un intervenant libre">
			<div css={rowStyle}>
				<ConditionalLoader data={hasRoles}>
					<div css={fieldStyle}>
						<Dropdown
							name="speakerRoleKey"
							label="Rôle *"
							options={speakerRoles}
							optionLabel="label"
							optionValue="key"
							value={roleKey}
							onChange={handleRoleChange}
							placeholder="Choisir un rôle"
							errorMessage={isTouched ? roleErrorMessage : null}
							isFormFieldValid={!isTouched || roleErrorMessage === null}
						/>
					</div>
				</ConditionalLoader>
				<div css={fieldStyle}>
					<InputText
						name="speakerDisplayName"
						label={hasRoles ? 'Nom' : 'Ou saisir un nom'}
						value={displayName}
						onChange={handleDisplayNameChange}
						maxLength={MAX_DISPLAY_NAME_LENGTH}
						placeholder={hasRoles ? 'Prénom Nom' : 'Aide-soignante, voisin, infirmière…'}
						errorMessage={isTouched ? errorMessage : null}
						isFormFieldValid={!isTouched || errorMessage === null}
					/>
				</div>
				<Button type="submit" label="Attribuer" />
			</div>
			<p css={noteStyle}>Attribuer ne relance aucun traitement : le découpage reste tel quel.</p>
		</Form>
	);
};

export default SpeakerAssignmentFreeNameField;
